import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorHandling } from "./ErrorHandling";
import { helper } from "@tripian/model";
import AppRouter from "./AppRouter/AppRouter";
import * as gtag from "../gtag";
import useScript from "./useScript";
import Notify from "./Notify/Notify";
import { useLocalStorage } from "../hooks/useLocalStorage";
import "./App.scss";
import useGoogleAnalytics from "./useGoogleAnalytics";

const preloadFont = (fontFamily: string) => {
  const fontWeights = {
    Geist: ["Regular", "Medium", "SemiBold", "Bold"],
    Herokid: ["Regular", "SemiBold"],
    Poppins: ["Regular", "Medium", "SemiBold", "Bold"],
  };

  const weights = fontWeights[fontFamily as keyof typeof fontWeights] || [];
  weights.forEach(weight => {
    // Webpack ile asset importu
    // @ts-ignore
    const fontUrl = require(`../assets/fonts/${fontFamily}-${weight}.woff`);
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "font";
    link.type = "font/woff";
    link.href = fontUrl;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  });
};

const App = (): JSX.Element => {
  useGoogleAnalytics(window.tconfig.GOOGLE_ANALYTICS_URL || "");
  useScript(window.tconfig.COOKIE_CONSENT_URL || "");
  const { getLocalData, setLocalData } = useLocalStorage<"light" | "dark">(
    "theme"
  );
  const theme = getLocalData();

  useEffect(() => {
    helper.setLogo(
      window.tconfig.IMAGE_PATHS.LOGO_PATH_DARK,
      window.tconfig.IMAGE_PATHS.LOGO_PATH_LIGHT
    );
    const fontFamily =
      window.tconfig.BRAND_NAME === "Visa"
        ? "Geist"
        : window.tconfig.BRAND_NAME === "bookbarbados"
          ? "Herokid"
          : "Poppins";
    document.documentElement.style.setProperty("--font-family", fontFamily);
    preloadFont(fontFamily);

    if (theme) {
      helper.setTheme(
        theme || window.tconfig.AVAILABLE_THEMES[0],
        window.tconfig.THEME
      );
    } else {
      setLocalData(window.tconfig.AVAILABLE_THEMES[0]);
      helper.setTheme(window.tconfig.AVAILABLE_THEMES[0], window.tconfig.THEME);
    }
  }, [setLocalData, theme]);

  useEffect(() => {
    gtag.pageview(window.location.pathname);
    document.documentElement.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // Google Tag Manager for visa
  useEffect(() => {
    if (window.tconfig?.ELIGIBILITY_TYPE !== "visa") return;

    const gtmId = "GTM-TBWD3K92";

    // Head script
    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(script);

    // Body noscript
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      script.remove();
      noscript.remove();
    };
  }, []);

  return (
    <div className="App">
      <ErrorBoundary
        FallbackComponent={ErrorHandling.ErrorFallback}
        onError={ErrorHandling.myErrorHandler}
      >
        <Notify />
        <AppRouter />
      </ErrorBoundary>
    </div>
  );
};

export default App;
